"use client";

import { useRouter } from "next/navigation";
import React, { useCallback, useMemo } from "react";

import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { DEFAULT_CASINO_VIEW, type CasinoView } from "@/domain/casino/constants";
import { CasinoGame } from "@/features/v2/home/components/CasinoSection/interface";

import FirstView from "./firsView/FirstView";
import GamesView from "./gamesView/GamesView";
import { core } from "./localization/core";

const WrapperView = ({ currentView, hasFilterParams, initialCasinoGames, initialZingaGames }: { currentView: CasinoView; hasFilterParams: boolean; initialCasinoGames?: CasinoGame[]; initialZingaGames?: CasinoGame[] }) => {
  const router = useRouter();

  React.useEffect(() => {
    if (currentView === DEFAULT_CASINO_VIEW && !hasFilterParams) {
      const newParams = new URLSearchParams();
      newParams.set("view", DEFAULT_CASINO_VIEW);
      newParams.set("category", "Para ti");
      router.replace(`/casino?${newParams.toString()}`);
    }
  }, [currentView, hasFilterParams, router]);

  const getTabClassName = useCallback(
    (tabId: string): string => {
      const isActive = currentView === tabId;
      return isActive ? "bg-linear-to-br from-primary-300 to-primary-600 px-3 py-1.5 rounded-lg text-white" : "px-3 py-1.5 rounded-lg text-default-900 hover:bg-default-100";
    },
    [currentView]
  );

  const tabToCategoryMap = useMemo(() => {
    return {
      [DEFAULT_CASINO_VIEW]: "Para ti",
      recomendaciones: "Recomendados",
      "nuevos-juegos": "Nuevos",
      "mas-como-tu-favorito": "Más como tu favorito",
      "nuevos-juegos-para-ti": "Nuevos Juegos para ti",
      "tus-gustos": "Tus gustos",
      tendencia: "Tendencia",
      "favoritos-olvidados": "Favoritos olvidados",
      "mecanica-favorita": "Mecánica favorita",
      "proveedor-favorito": "Proveedor favorito",
      "mejores-en-tu-ubicacion": "Mejores en tu ubicación",
      juegos: "Todos los juegos"
    } as Record<string, string>;
  }, []);

  const handleTabClick = useCallback(
    (tabId: string) => {
      if (currentView === tabId) {
        return;
      }

      const newParams = new URLSearchParams();

      if (tabId === DEFAULT_CASINO_VIEW) {
        newParams.set("view", DEFAULT_CASINO_VIEW);
      } else {
        newParams.set("view", tabId);

        const category = tabToCategoryMap[tabId];
        if (category) {
          newParams.set("category", category);
        }
      }

      const queryString = newParams.toString();
      const newUrl = queryString ? `?${queryString}` : "";

      router.push(`/casino${newUrl}`);
    },
    [currentView, router, tabToCategoryMap]
  );

  const tabsList = useMemo(() => {
    return core.tabs
      .filter((tab) => tab.id !== "lobby")
      .map((tab) => {
        const isLongLabel = tab.label === "Nuevos juegos" || tab.label === "Recomendaciones";
        return (
          <CarouselItem
            key={tab.id}
            className={`${getTabClassName(tab.id)} ${isLongLabel ? "max-w-40" : "max-w-24"} text-center cursor-pointer`}
            onClick={() => {
              return handleTabClick(tab.id);
            }}
          >
            {tab.label}
          </CarouselItem>
        );
      });
  }, [getTabClassName, handleTabClick]);

  return (
    <>
      <div className="w-full max-w-sm md:max-w-7xl mx-auto flex flex-col px-4 gap-5 py-1.5 items-center justify-center text-default-900 bg-default-50 mt-4 md:mt-10 md:-mb-5 rounded-xl">
        <Carousel className="w-full mx-auto">
          <CarouselContent className="flex items-center justify-start md:justify-center px-4 w-full gap-5 py-1">{tabsList}</CarouselContent>
        </Carousel>
      </div>
      {hasFilterParams || currentView === DEFAULT_CASINO_VIEW ? <GamesView currentView={currentView} initialCasinoGames={initialCasinoGames} initialZingaGames={initialZingaGames} /> : <FirstView initialCasinoGames={initialCasinoGames} initialZingaGames={initialZingaGames} />}
    </>
  );
};

export default WrapperView;
